import type { PropsWithChildren } from "react";
import { createContext, useCallback, useMemo } from "react";
import type { AccessPermission } from "@entrilia/shared/api/types";
import { getCategoryTagColor } from "@entrilia/shared/utilities/categoryColor";
import type { Category, ResolvedCategory } from "../api/types/accessTypes";
import type {
  ClientDictionaries,
  ClientInfo,
  ClientSettings,
  FeatureToggle,
  MenuBadges,
} from "../api/types/clientTypes";
import type { ClientPermissionSet } from "../api/types/userTypes";
import useUserContext from "./useUserContext";

export interface Client extends ClientInfo, ClientPermissionSet {}

interface Props {
  client: Client;
  clientSettings: ClientSettings;
  clientDictionaries: ClientDictionaries;
  accessCategories: Category[];
  refreshAccessCategories: () => Promise<void>;
  menuBadges: MenuBadges | undefined;
  updateMenuBadges: () => void;
}

interface ContextValue extends Client {
  isAllowedToAccessAllEntities: boolean;
  isFundAdmin: boolean;
  hasPermissions: (permissions: AccessPermission[]) => boolean;
  hasAnyPermission: (permissions: AccessPermission[]) => boolean;
  settings: ClientSettings;
  dictionaries: ClientDictionaries;
  accessCategories: ResolvedCategory[];
  getAccessCategoryByExternalId: (externalCategoryId: string | undefined) => ResolvedCategory | undefined;
  getAccessCategoryById: (categoryId: string | undefined) => ResolvedCategory | undefined;
  refreshAccessCategories: () => Promise<void>;
  menuBadges: MenuBadges | undefined;
  updateMenuBadges: () => void;
  isPortalFeatureEnabled: (feature: FeatureToggle) => boolean;
}

const ClientContext = createContext<ContextValue | undefined>(undefined);

export const ClientContextProvider = ({
  children,
  client,
  clientSettings,
  clientDictionaries,
  accessCategories,
  refreshAccessCategories,
  menuBadges,
  updateMenuBadges,
}: PropsWithChildren<Props>) => {
  const { isBetaAccessEnabled } = useUserContext();

  const hasPermissions = useCallback(
    (permissions: AccessPermission[]) => permissions.every((p) => client.permissions.includes(p)),
    [client.permissions]
  );

  const hasAnyPermission = useCallback(
    (permissions: AccessPermission[]) => permissions.some((p) => client.permissions.includes(p)),
    [client.permissions]
  );

  const isPortalFeatureEnabled = useCallback(
    (feature: FeatureToggle) =>
      clientSettings.enabledFeatures.includes(feature) ||
      (isBetaAccessEnabled && clientSettings.betaOnlyFeatures.includes(feature)),
    [clientSettings.betaOnlyFeatures, clientSettings.enabledFeatures, isBetaAccessEnabled]
  );

  const accessCategoriesWithColor = useMemo(
    () =>
      accessCategories.map((category) => ({
        ...category,
        effectiveColor: getCategoryTagColor(category.color),
      })),
    [accessCategories]
  );

  const getAccessCategoryByExternalId = useCallback(
    (externalCategoryId: string | undefined) =>
      externalCategoryId ? accessCategoriesWithColor.find((c) => c.externalId === externalCategoryId) : undefined,
    [accessCategoriesWithColor]
  );

  const getAccessCategoryById = useCallback(
    (categoryId: string | undefined) =>
      categoryId ? accessCategoriesWithColor.find((c) => c.id === categoryId) : undefined,
    [accessCategoriesWithColor]
  );

  const isAllowedToAccessAllEntities = !client.entityLevelAccess || client.allowAccessToAllEntities === true;

  return (
    <ClientContext
      value={{
        ...client,
        isAllowedToAccessAllEntities,
        settings: clientSettings,
        dictionaries: clientDictionaries,
        accessCategories: accessCategoriesWithColor,
        getAccessCategoryByExternalId,
        getAccessCategoryById,
        refreshAccessCategories,
        menuBadges,
        updateMenuBadges,
        hasPermissions,
        hasAnyPermission,
        isFundAdmin: client.type === "FundAdmin",
        isPortalFeatureEnabled,
      }}
    >
      {children}
    </ClientContext>
  );
};

export default ClientContext;
